import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import PortalShell from './PortalShell';
import { useAuth } from '@/contexts';
import { useLanguage } from '@/contexts/LanguageContext';
import { authApi, loyaltyApi, PortalItem, portalApi } from '@/lib';
import { LoyaltyEnrolledMembership, LoyaltyPrefill, LoyaltyProgram } from '@/types';

export interface LoyaltyOutletState {
  program: LoyaltyProgram | null;
  enrolledMembership: LoyaltyEnrolledMembership | null;
  prefill: LoyaltyPrefill | null;
}

export interface PortalOutletContext {
  items: PortalItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  loyalty: LoyaltyOutletState;
  /** True while the very first loyalty fetch is in flight. Lets pages render
   * a spinner instead of mistaking the initial null program for "unavailable". */
  loyaltyLoading: boolean;
  refetchLoyalty: () => Promise<void>;
}

// Layout route for /portal/home/* — fetches portal items + loyalty state once
// and keeps the shell (sidebar + mobile top bar) mounted across child route
// transitions. Children read state and trigger live updates via
// useOutletContext<PortalOutletContext>().
export default function PortalShellContainer() {
  const { t } = useTranslation('portal');
  const { language } = useLanguage();
  const { authedCode, refreshToken } = useAuth();
  const [items, setItems] = useState<PortalItem[]>([]);
  const [loyalty, setLoyalty] = useState<LoyaltyOutletState>({
    program: null,
    enrolledMembership: null,
    prefill: null,
  });
  const [loading, setLoading] = useState(true);
  // Tracks the very first loyalty fetch separately from `loading` so a child
  // route (e.g. /portal/home/loyalty) can show a spinner instead of treating
  // an unresolved null program as "program unavailable". Flips false once,
  // never back; refetches on tab-focus keep the previous value in place.
  const [loyaltyLoading, setLoyaltyLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Loyalty visibility is driven entirely by the backend response: if the
  // tenant has a row in direct_booking_loyalty_programs, /loyalty/program
  // returns it and the portal renders the surface. No frontend allowlist.

  const refetch = useCallback(
    async (shouldApply: () => boolean = () => true) => {
      try {
        const data = await portalApi.getUi(language);
        if (!shouldApply()) return;
        setItems(data.items);
        setError(null);
      } catch (err) {
        if (!shouldApply()) return;
        console.debug('portal: refetch failed', err);
        setError(t('We couldn’t load your portal right now.'));
      }
    },
    [t, language]
  );

  const refetchLoyalty = useCallback(
    async (shouldApply: () => boolean = () => true) => {
      try {
        const data = await loyaltyApi.getProgram();
        if (!shouldApply()) return;
        setLoyalty({
          program: data.program,
          enrolledMembership: data.enrolledMembership,
          prefill: data.prefill,
        });
      } catch (err) {
        // Preserve the last known good state on transient failures (flaky
        // network, momentary 5xx on tab focus). Resetting to null here would
        // make the Member badge disappear and the join prompts reappear
        // mid-session. Initial-load failure naturally leaves the state at
        // its initial null, which already hides the surface correctly.
        console.debug('portal: loyalty refetch failed; keeping last state', err);
      }
    },
    []
  );

  // BES-7770: the cached JWT's scopes are frozen at mint time. If the host
  // enables a section mid-session, re-mint so the newly shown section is
  // actually openable. Silent + fail-safe: keep current token on error.
  const refreshScopes = useCallback(async () => {
    if (!authedCode) return;
    try {
      const { token } = await authApi.validateCode(authedCode, 'portal');
      refreshToken(token);
    } catch (err) {
      console.debug('portal: token scope refresh failed; keeping current token', err);
    }
  }, [authedCode, refreshToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.all([refetch(() => !cancelled), refetchLoyalty(() => !cancelled), refreshScopes()]);
      if (!cancelled) {
        setLoading(false);
        setLoyaltyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refetch, refetchLoyalty, refreshScopes]);

  // Safety net: if the guest flips back from Stripe / another tab, pull fresh
  // status so completions triggered by webhooks or async backend events land.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void refetch();
        void refetchLoyalty();
        void refreshScopes();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [refetch, refetchLoyalty, refreshScopes]);

  return (
    <PortalShell items={items} loyalty={loyalty}>
      <Outlet
        context={
          {
            items,
            loading,
            error,
            refetch,
            loyalty,
            loyaltyLoading,
            refetchLoyalty,
          } satisfies PortalOutletContext
        }
      />
    </PortalShell>
  );
}
